import { Label } from "@/components/ui/label"
import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"
import { V2MarketType, V2InputType } from "@/packaging-v2/lib/schema"
import { V2ItemFormData, V2SectionFormData, V2ParamFormData } from "@/store/packaging-v2"
import { V2Product, V2SKU } from "@/packaging-v2/lib/schema"
import { FormParamStatus } from "@/lib/form/types"
import RadioGroup from "@/components/radio-group"
import Select from "@/components/select"
import { Icon } from "@/icons/icon"


export interface ItemHeaderProps {
  className?: string
  item: V2ItemFormData
  products: V2Product[]
  infoFieldsSection?: V2SectionFormData
  disabled?: boolean
  canDelete?: boolean
  isDeleteLoading?: boolean
  onMarketTypeChange?: (marketType: V2MarketType) => void
  onProductChange?: (productId: string) => void
  onSKUChange?: (skuId: string) => void
  onInfoFieldChange?: (paramId: string, value: string) => void
  onDelete?: () => void
}

/**
 * Delete button component with accessibility attributes
 */
interface DeleteButtonProps {
  canDelete?: boolean
  disabled?: boolean
  loading?: boolean
  onDelete?: () => void
}

function DeleteButton({ canDelete, disabled, loading, onDelete }: DeleteButtonProps) {
  if (!canDelete) return null

  return (
    <button
      className="hover:cursor-pointer text-button disabled:opacity-50 disabled:cursor-not-allowed"
      type="button"
      disabled={disabled || loading}
      onClick={onDelete}
      aria-label="Delete item"
    >
      <Icon name="TrashCan" className="h-4 w-auto" />
    </button>
  )
}

/**
 * Item header component for displaying and editing item details
 * Includes market type toggle, product/SKU dropdowns, and dynamic info fields
 */
export default function ItemHeader({
  className,
  item,
  products,
  infoFieldsSection,
  disabled = false,
  canDelete = false,
  isDeleteLoading = false,
  onMarketTypeChange,
  onProductChange,
  onSKUChange,
  onInfoFieldChange,
  onDelete,
}: ItemHeaderProps) {
  const isSubmitted = item.status === FormParamStatus.SUBMITTED

  // Get SKUs for selected product
  const selectedProduct = products.find((p) => p.id === item.productId)
  const availableSKUs: V2SKU[] = selectedProduct?.skus || []

  // Filter INFORMATION_FIELDS parameters - skip hardcoded fields
  const additionalInfoFields = infoFieldsSection?.parameters.filter((param) => {
    const skipNames = ["market type", "product", "sku"]
    return !skipNames.some((name) => param.name.toLowerCase() === name)
  }) || []

  function handleMarketTypeChange(value: string) {
    if (onMarketTypeChange && !disabled) {
      onMarketTypeChange(value as V2MarketType)
    }
  }

  function handleProductChange(value: string) {
    if (onProductChange && !disabled) {
      onProductChange(value)
    }
  }

  function handleSKUChange(value: string) {
    if (onSKUChange && !disabled) {
      onSKUChange(value)
    }
  }

  // Readonly mode - matches old packaging style
  if (isSubmitted) {
    return (
      <div className={cn("bg-white rounded-lg border overflow-hidden", className)}>
        {/* Section header - matches old packaging style */}
        <h3 className="flex justify-between items-center py-2.5 px-3 bg-band-fill uppercase text-sm font-semibold">
          <span>Item Information</span>
          <DeleteButton
            canDelete={canDelete}
            disabled={disabled || isSubmitted}
            loading={isDeleteLoading}
            onDelete={onDelete}
          />
        </h3>

        {/* Grid layout like old packaging */}
        <div className="grid grid-cols-2 gap-x-3 gap-y-4 px-3 pt-3 pb-4">
          <div className="flex flex-col gap-1">
            <Label className="text-sm font-medium">Market Type</Label>
            <p className="text-sm font-medium">{item.marketType}</p>
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-sm font-medium">Product</Label>
            <p className="text-sm font-medium">{item.productName || "-"}</p>
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-sm font-medium">SKU</Label>
            <p className="text-sm font-medium">{item.skuName || "-"}</p>
          </div>

          {/* Additional info fields */}
          {additionalInfoFields.map((param) => (
            <div key={param.id} className="flex flex-col gap-1">
              <Label className="text-sm font-medium">{param.name}</Label>
              <p className="text-sm font-medium">{param.value || "-"}</p>
            </div>
          ))}


        </div>
      </div>
    )
  }

  // Edit mode - existing layout
  return (
    <div className={cn("bg-white rounded-lg border overflow-hidden", className)}>
      {/* Header with delete button */}
      <div className="flex justify-between items-center py-2.5 px-3 bg-band-fill">
        <span className="uppercase text-sm font-semibold">Item Information</span>
        <DeleteButton
          canDelete={canDelete}
          disabled={disabled || isSubmitted}
          loading={isDeleteLoading}
          onDelete={onDelete}
        />
      </div>

      <div className="space-y-4 p-4">
      {/* Market Type Radio */}
      <div className="flex items-center gap-4">
        <Label className="basis-1/3 text-sm font-medium">Market Type</Label>
        <div className="basis-2/3">
          <RadioGroup
            options={[
              { label: "Domestic", value: V2MarketType.DOMESTIC },
              { label: "Export", value: V2MarketType.EXPORT },
            ]}
            value={item.marketType}
            disabled={disabled}
            onChange={handleMarketTypeChange}
          />
        </div>
      </div>

      {/* Two Column Layout for remaining fields */}
      <div className="grid grid-cols-2 gap-4">
        {/* Product Dropdown */}
        <div className="flex flex-col gap-2">
          <Label className="text-sm font-medium truncate" title="Product">Product</Label>
          <div>
            <Select
              placeholder="Select Product"
              options={products.map((p) => ({ label: p.name, value: p.id }))}
              value={item.productId}
              disabled={disabled}
              onChange={handleProductChange}
            />
          </div>
        </div>

        {/* SKU Dropdown */}
        <div className="flex flex-col gap-2">
          <Label className="text-sm font-medium truncate" title="SKU">SKU</Label>
          <div>
            <Select
              placeholder="Select SKU"
              options={availableSKUs.map((sku) => ({
                label: `${sku.name} (${sku.code})`,
                value: sku.id,
              }))}
              value={item.skuId}
              disabled={disabled || !item.productId}
              onChange={handleSKUChange}
            />
          </div>
        </div>

        {/* Additional Information Fields (Vendor, etc.) */}
        {additionalInfoFields.map((param) => (
          <InfoFieldInput
            key={param.id}
            param={param}
            disabled={disabled}
            onValueChange={(value) => onInfoFieldChange?.(param.id, value)}
          />
        ))}


      </div>
      </div>
    </div>
  )
}

/**
 * Input component for additional INFORMATION_FIELDS parameters
 * Uses Select component for all dropdowns
 * Designed for grid layout with label on top
 */
function InfoFieldInput({
  param,
  disabled,
  onValueChange,
}: {
  param: V2ParamFormData
  disabled: boolean
  onValueChange?: (value: string) => void
}) {
  const isSubmitted = param.status === FormParamStatus.SUBMITTED

  // Handle DROPDOWN type
  if (param.inputType === V2InputType.DROPDOWN) {
    const options = param.possibleValues?.map((v) => ({ label: v, value: v })) || []

    if (isSubmitted) {
      return (
        <div className="flex flex-col gap-2">
          <Label className="text-sm font-medium truncate" title={param.name}>{param.name}</Label>
          <div>
            <span className="font-medium text-sm">{param.value || "-"}</span>
          </div>
        </div>
      )
    }

    // Use Select for all dropdowns
    return (
      <div className="flex flex-col gap-2">
        <Label className="text-sm font-medium truncate" title={param.name}>{param.name}</Label>
        <div>
          <Select
            placeholder={`Select ${param.name}`}
            options={options}
            value={param.value}
            disabled={disabled}
            onChange={(value) => onValueChange?.(value)}
          />
        </div>
      </div>
    )
  }

  // Handle submitted state for other types
  if (isSubmitted) {
    return (
      <div className="flex flex-col gap-2">
        <Label className="text-sm font-medium truncate" title={param.name}>{param.name}</Label>
        <div>
          <span className="font-medium text-sm">{param.value || "-"}</span>
        </div>
      </div>
    )
  }

  // Handle text/number inputs
  return (
    <div className="flex flex-col gap-2">
      <Label className="text-sm font-medium truncate" title={param.name}>{param.name}</Label>
      <div>
        <Input
          type={param.inputType === V2InputType.VALUE_NUMBER ? "number" : "text"}
          value={param.value || ""}
          disabled={disabled}
          onChange={(e) => onValueChange?.(e.target.value)}
        />
      </div>
    </div>
  )
}

/**
 * Read-only version for history view
 */
export function ItemHeaderReadOnly({
  className,
  item,
}: {
  className?: string
  item: V2ItemFormData
}) {
  return (
    <div className={cn("space-y-2 p-4 bg-gray-50 rounded-lg", className)}>
      <div className="flex justify-between">
        <span className="text-sm text-gray-600">Type</span>
        <span className="font-medium">{item.marketType}</span>
      </div>
      <div className="flex justify-between">
        <span className="text-sm text-gray-600">Product</span>
        <span className="font-medium">{item.productName || "-"}</span>
      </div>
      <div className="flex justify-between">
        <span className="text-sm text-gray-600">SKU</span>
        <span className="font-medium">{item.skuName || "-"}</span>
      </div>

    </div>
  )
}
